import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { setStructuredData, clearStructuredData } from "@/lib/structuredData";
import { AuroraBackground } from "@/components/AuroraBackground";
import { Reveal } from "@/components/Reveal";
import { StoreBadgeGroup } from "@/components/StoreBadges";
import { HOME } from "@/constants/testIds";
import { ChevronDown, Volume2, Square } from "lucide-react";

const QUESTIONS = ["one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten"];
const EXTRA_KEYS = ["11", "12", "13", "14"];

const FaqItem = ({ id, q, a, isOpen, onToggle, isSpeaking, onListen, listenLabel, stopLabel }) => (
  <div className="aurora-card bg-[var(--aurora-coach)]/60 border border-white/8 overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full flex items-start justify-between gap-4 p-5 lg:p-6 text-left"
      data-testid={`faq-q-${id}`}
      aria-expanded={isOpen}
    >
      <span className="font-serif text-lg lg:text-xl flex-1 leading-snug">{q}</span>
      <ChevronDown size={18} className={`mt-1.5 shrink-0 text-[var(--aurora-gold)] transition-transform ${isOpen ? "rotate-180" : ""}`} />
    </button>
    {isOpen && (
      <div className="px-5 lg:px-6 pb-6 flex flex-col gap-3" data-testid={`faq-a-${id}`}>
        <p className="text-white/75 font-light leading-relaxed">{a}</p>
        <button
          onClick={() => onListen(`${q} ${a}`)}
          className="inline-flex w-fit items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-[var(--aurora-cyan)] hover:text-white transition"
          data-testid={`faq-listen-${id}`}
        >
          {isSpeaking ? <Square size={12} /> : <Volume2 size={12} />}
          {isSpeaking ? stopLabel : listenLabel}
        </button>
      </div>
    )}
  </div>
);

const FaqPage = () => {
  const { t } = useTranslation(["landing", "site"]);
  const [open, setOpen] = useState(0);
  const [speakingId, setSpeakingId] = useState(null);

  const onListen = (text, id) => {
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    if (speakingId === id) { window.speechSynthesis.cancel(); setSpeakingId(null); return; }
    window.speechSynthesis.cancel();
    const utt = new SpeechSynthesisUtterance(text);
    utt.onend = () => setSpeakingId(null);
    utt.onerror = () => setSpeakingId(null);
    setSpeakingId(id);
    window.speechSynthesis.speak(utt);
  };

  const allQs = [
    ...QUESTIONS.map((k, i) => ({ id: `q-${i}`, q: t(`landing:faq.${k}.q`), a: t(`landing:faq.${k}.a`) })),
    ...EXTRA_KEYS.map((k, i) => ({ id: `ex-${i}`, q: t(`site:faqExtra.q${k}`), a: t(`site:faqExtra.a${k}`) })),
  ];

  useEffect(() => {
    const mainEntity = allQs
      .filter((item) => item.q && item.a && !/^(landing|site):/.test(item.q) && !/^(landing|site):/.test(item.a))
      .map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      }));
    if (mainEntity.length) {
      setStructuredData("faq", {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity,
      });
    }
    return () => clearStructuredData("faq");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  return (
    <>
      <section className="relative pb-14">
        <AuroraBackground variant="soft" />
        <div className="relative mx-auto max-w-5xl px-5 lg:px-8 pt-8 lg:pt-12 text-center">
          <Reveal>
            <p className="eyebrow">{t("landing:faq.kicker", "FAQ")}</p>
            <h1 className="mt-3 font-serif text-[2.4rem] sm:text-5xl lg:text-[4rem] tracking-tight font-light leading-[1.05]">
              {t("site:faqExtra.heroTitle1")}{" "}<em className="italic aurora-shimmer-text">{t("site:faqExtra.heroTitle2")}</em>
            </h1>
            <p className="mt-5 text-white/72 text-lg font-light max-w-2xl mx-auto">{t("landing:faq.subtitle")}</p>
          </Reveal>
        </div>
      </section>

      <section className="relative pb-20">
        <div className="mx-auto max-w-3xl px-5 lg:px-8" data-testid={HOME.faqAccordion}>
          <div className="flex flex-col gap-3">
            {allQs.map((item, idx) => (
              <FaqItem
                key={item.id}
                id={item.id}
                q={item.q}
                a={item.a}
                isOpen={open === idx}
                onToggle={() => setOpen(open === idx ? -1 : idx)}
                isSpeaking={speakingId === item.id}
                onListen={(text) => onListen(text, item.id)}
                listenLabel={t("site:faqExtra.listen")}
                stopLabel={t("site:faqExtra.stop")}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-20">
        <div className="mx-auto max-w-3xl px-5 lg:px-8 text-center">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight font-light">
              {t("site:faqExtra.closingTitle1")}{" "}<em className="italic text-[var(--aurora-gold)]">{t("site:faqExtra.closingTitle2")}</em>
            </h2>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <StoreBadgeGroup startLabel={t("site:common.startFree")} />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default FaqPage;
